<template>
    <div class="lb-game-card">
        <header>
            <div class="level">
                <div class="level-left">
                    <span
                        v-if="isSimulation || closeable"
                        class="icon is-medium has-text-black is-clickable"
                        @click="$emit('stopSimulation')"
                    >
                        <i class="mdi mdi-24px mdi-close"></i>
                    </span>
                    <p
                        v-if="timeLimit"
                        :class="{
                            'has-text-danger animated flash infinite slow':
                                inDanger,
                        }"
                        class="level-item"
                    >{{ formattedTime }}</p>
                </div>
                <user-mini-profile v-if="content.owner" :user="content.owner" class="level-right" />
            </div>
        </header>
        <div class="card-content is-relative">
            <div class="content">
                <p
                    class="title lb-alternative-font is-size-5 has-text-weight-bold"
                >{{ content.title }}</p>
                <p class="content has-text-grey" v-html="content.description" />
                <object-image-gallery :images="content.images" class="pb-3" />
                <vue-plyr v-if="content.video">
                    <div class="plyr__video-embed">
                        <iframe
                            :src="`https://www.youtube.com/embed/${getIdFromURL(
                                content.video
                            )}?iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0&enablejsapi=1`"
                            allowfullscreen
                            allowtransparency
                            allow="autoplay"
                        />
                    </div>
                </vue-plyr>
                <vue-plyr v-if="content.audio" ref="audio-plyr" class="is-block">
                    <audio>
                        <source :src="content.audio.url" :type="content.audio.mimeType" />
                    </audio>
                </vue-plyr>
                <!--<figure
                    v-if="!locationIsEmpty && trigger != 'AT_ARRIVE' && showMap"
                    class="image is-3by2"
                >
                    <img :src="staticMapURL" style="object-fit: cover" />
                </figure>-->
                <div v-if="content.url" class="has-text-centered lb-media-link">
                    <a :href="content.url" target="_blank" class="button is-link is-fullwidth">
                        <span class="icon">
                            <i class="fas fa-link" />
                        </span>
                        <span>{{ $t('Object.url_more_info') }}</span>
                    </a>
                </div>
                <div v-for="(file, index) in content.files" :key="index" class="lb-media-link">
                    <a :href="file.url" target="_blank" class="button is-link is-fullwidth">
                        <span class="icon">
                            <i class="fas fa-file-pdf" />
                        </span>
                        <span>{{ file.name }}</span>
                    </a>
                </div>
                <component :is="answerComponent" :answers="content.answers" @answer="emitAnswer" />
            </div>
        </div>
        <slot></slot>
    </div>
</template>

<script>
import UserMiniProfile from '@/components/UserMiniProfile';
import ObjectImageGallery from '@/components/ObjectImageGallery';
import SessionObjectCardSingleAnswer from '@/components/SessionObjectCardSingleAnswer';
import SessionObjectCardMultipleAnswer from '@/components/SessionObjectCardMultipleAnswer';
import SessionObjectCardHangman from '@/components/SessionObjectCardHangman';
import SessionObjectCardFreetext from '@/components/SessionObjectCardFreetext';
import { staticMap } from '@/services/API';
import { isEmpty, filter, isEqual, contains, keys } from 'underscore';
import { getDistance, getBounds, getCenterOfBounds } from 'geolib';
import moment from 'moment';
export default {
    components: {
        ObjectImageGallery,
        UserMiniProfile,
        SessionObjectCardSingleAnswer,
        SessionObjectCardMultipleAnswer,
        SessionObjectCardHangman,
        SessionObjectCardFreetext,
    },
    props: {
        content: {
            type: Object,
            default() {
                return {};
            },
        },
        timeLimit: {
            type: Number,
            default: 0,
        },
        timeUnits: {
            type: String,
            default: 'minutes',
        },
        isSimulation: {
            type: Boolean,
            default: false,
        },
        showMap: {
            type: Boolean,
            default: true,
        },
        closeable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            time: undefined,
            timeInterval: 1000,
            interval: undefined,
            elapsedTime: 0,
            showAnimation: false,
            selectedAnswers: [],
        };
    },
    computed: {
        duration() {
            return moment.duration(this.timeLimit, this.timeUnits || 'minutes');
        },
        minutes() {
            return this.remainingDuration.minutes();
        },
        seconds() {
            return this.remainingDuration.seconds();
        },
        inDanger() {
            return this.minutes == 0 && this.seconds < 10;
        },
        formattedTime() {
            let minutes = this.minutes;
            let seconds = this.seconds;
            minutes = minutes.toString().length == 1 ? '0' + minutes : minutes;
            seconds = seconds.toString().length == 1 ? '0' + seconds : seconds;
            return `${minutes}:${seconds}`;
        },
        remainingDuration() {
            return moment.duration(
                this.duration - this.elapsedTime,
                'milliseconds'
            );
        },
        /*staticMapURL() {
            return staticMap(
                this.content.location.longitude,
                this.content.location.latitude,
                this.content.location.zoomLevel
            );
        },*/
        locationIsEmpty() {
            return isEmpty(this.content.location);
        },
        trigger() {
            return this.content.triggers && keys(this.content.triggers)[0];
        },
        answerComponent() {
            const answerType = this.content.answerType || 'SINGLE';
            switch (answerType.toUpperCase()) {
                case 'SINGLE':
                    return SessionObjectCardSingleAnswer;
                case 'MULTIPLE':
                    return SessionObjectCardMultipleAnswer;
                case 'HANGMAN':
                    return SessionObjectCardHangman;
                case 'FREETEXT':
                    return SessionObjectCardFreetext;
            }
            return SessionObjectCardMultipleAnswer;
        },
    },
    watch: {
        seconds() {
            if (this.seconds == 0 && this.minutes == 0) {
                clearInterval(this.interval);
                this.$emit('timeout');
            }
        },
    },
    mounted() {
        this.time = new Date();
        if (this.timeLimit) {
            this.interval = setInterval(() => {
                this.elapsedTime += this.timeInterval;
            }, this.timeInterval);
        }
    },
    beforeDestroy() {
        this.interval && clearInterval(this.interval);
    },
    methods: {
        emitAnswer({ answer, correct }) {
            /*if (!contains(this.selectedAnswers, answer)) {
        this.selectedAnswers.push(answer);
      }*/
            this.$emit('answer', {
                answer: {
                    answer,
                    object: this.content._id,
                    time: new Date() - this.time,
                    points: correct ? 10 : 0,
                },
                trigger: this.trigger,
            });
            //this.selectedAnswers = [];
        },
        getIdFromURL(url) {
            const youtubeRegexp =
                /https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/gi;

            let id = url.replace(youtubeRegexp, '$1');

            if (id.includes(';')) {
                const pieces = id.split(';');

                if (pieces[1].includes('%')) {
                    const uriComponent = decodeURIComponent(pieces[1]);
                    id = `http://youtube.com${uriComponent}`.replace(
                        youtubeRegexp,
                        '$1'
                    );
                } else {
                    id = pieces[0];
                }
            } else if (id.includes('#')) {
                id = id.split('#')[0];
            }
            return id;
        },
    },
};
</script>

<style scoped></style>
